import {
  IRequisition,
  IRequisitionData,
  IRequisitionLine,
  IRequisitionLineData,
  IRequisitionStatus,
  IRequisitionStatusData,
  IRequisitionFieldError,
  IRequisitionFieldErrorData,
  IRequisitionLineFieldError,
  IRequisitionLineFieldErrorData,
  RequisitionStatusEnum,
  IRequisitionSupplierAddress,
  IRequisitionSupplierAddressData,
  IRequisitionSupplierContact,
  IRequisitionSupplierContactData,
  IRequisitionSupplier,
  IRequisitionSupplierData,
  IRequisitionSupplierFieldError,
  IRequisitionSupplierAddressFieldError,
  IRequisitionSupplierContactFieldError,
  IRequisitionSupplierFieldErrorData,
  IRequisitionSupplierAddressFieldErrorData,
  IRequisitionSupplierContactFieldErrorData,
  IRequisitionAttachment,
  IRequisitionAttachmentData,
  IRequisitionApproval,
  IRequisitionApprovalData,
  RequisitionApprovalState,
  IRequisitionComment,
  IRequisitionCommentData
} from './requisition.types';

import {
  IDepartment,
  Department,
  ICurrency,
  Currency,
  GLAccount,
  IGLAccount,
  User,
  IUser,
  Country,
  ICountry,
  FieldErrors,
  IFieldErrors,
  IAttachmentData,
  IAttachment
} from '@/models';

import { MentionCommentMixin } from '../mention/mention';
import { AbstractAttachment } from '../attachment/attachment';

import moment from 'moment';
export class RequisitionSupplierAddress implements IRequisitionSupplierAddress {
  readonly id?: number;
  readonly country?: ICountry | string;

  readonly address1: string = '';
  readonly address2: string = '';
  readonly city: string = '';
  readonly region: string = '';
  readonly postal_code: string = '';

  constructor(data?: IRequisitionSupplierAddressData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.address1) {
        this.address1 = data.address1;
      }

      if (data.address2) {
        this.address2 = data.address2;
      }

      if (data.city) {
        this.city = data.city;
      }

      if (data.region) {
        this.region = data.region;
      }

      if (data.country) {
        this.country =
          typeof data.country === 'string'
            ? data.country
            : new Country(data.country);
      }

      if (data.postal_code) {
        this.postal_code = data.postal_code;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class RequisitionSupplierContact implements IRequisitionSupplierContact {
  readonly id?: number;
  readonly name: string = '';
  readonly last_name: string = '';
  readonly email: string = '';
  readonly phone: string = '';

  constructor(data?: IRequisitionSupplierContactData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.name) {
        this.name = data.name;
      }

      if (data.email) {
        this.email = data.email;
      }

      if (data.phone) {
        this.phone = data.phone;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class RequisitionSupplier implements IRequisitionSupplier {
  readonly id?: number;
  readonly name: string = '';
  readonly vendor_code: string = '';
  readonly address: IRequisitionSupplierAddress = new RequisitionSupplierAddress();
  readonly contact: IRequisitionSupplierContact = new RequisitionSupplierContact();

  constructor(data?: IRequisitionSupplierData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.name) {
        this.name = data.name;
      }

      if (data.vendor_code) {
        this.vendor_code = data.vendor_code;
      }

      if (data.address) {
        this.address = new RequisitionSupplierAddress(data.address);
      }

      if (data.contact) {
        this.contact = new RequisitionSupplierContact(data.contact);
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class RequisitionLine implements IRequisitionLine {
  readonly id?: number;
  readonly item: string = '';
  readonly description: string = '';
  readonly quantity: number = 0;
  readonly unit_price: number = 0;
  readonly uom: string = 'EA';
  readonly total_price: number = 0;
  readonly account: IGLAccount | number | null = null;
  readonly department: IDepartment | number | null = null;

  constructor(data?: IRequisitionLineData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.account) {
        this.account =
          typeof data.account === 'number'
            ? data.account
            : new GLAccount(data.account);
      }

      if (data.department) {
        this.department =
          typeof data.department === 'number'
            ? data.department
            : new Department(data.department);
      }

      this.item = data.item;
      this.description = data.description;
      this.quantity = data.quantity;
      this.unit_price = Number(data.unit_price);
      this.uom = data.uom;

      if (data.total_price) {
        this.total_price = data.total_price;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class RequisitionStatus implements IRequisitionStatus {
  readonly value: number = RequisitionStatusEnum.Draft;
  readonly display_name: string = 'Draft';

  constructor(data?: IRequisitionStatusData) {
    if (data) {
      this.value = data.value;
      this.display_name = data.display_name;
    }
  }

  // Returns color based on state
  getColor(): string {
    switch (this.value) {
      case RequisitionStatusEnum.Draft:
        return 'draft';

      case RequisitionStatusEnum.Submitted:
      case RequisitionStatusEnum.InReview:
      case RequisitionStatusEnum.Approval2Pending:
      case RequisitionStatusEnum.Approval3Pending:
      case RequisitionStatusEnum.Approval4Pending:
        return 'submitted';

      case RequisitionStatusEnum.Approved:
        return 'approved';

      case RequisitionStatusEnum.Rejected:
        return 'reject';

      case RequisitionStatusEnum.BuyerAssigned:
        return 'teal';

      case RequisitionStatusEnum.PurchaseInProgress:
        return 'blue darken-3';

      case RequisitionStatusEnum.PurchaseDelivered:
        return 'done';

      case RequisitionStatusEnum.Cancelled:
        return 'cancel';

      case RequisitionStatusEnum.Received:
        return 'done';

      default:
        return 'white';
    }
  }
}

export class RequisitionAttachment implements IRequisitionAttachment {
  readonly id?: number | undefined;
  readonly file: string | File;
  readonly filename: string = '';

  constructor(data: IRequisitionAttachmentData) {
    if (data.id) {
      this.id = data.id;
    }

    if (data.filename) {
      this.filename = data.filename;
    }

    this.file = data.file;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class RequisitionCommentAttachment extends AbstractAttachment {
  constructor(data: IAttachmentData) {
    super(data);
  }

  toString(): string {
    return this.filename;
  }
}

export class RequisitionComment extends MentionCommentMixin
  implements IRequisitionComment {
  readonly id?: number;
  readonly owner?: string;
  readonly text: string = '';
  readonly attachments: IAttachment[] = [];
  readonly created_at?: Date | string;
  readonly updated_at?: Date | string;

  constructor(data?: IRequisitionCommentData) {
    super(data);
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.owner) {
        this.owner = data.owner;
      }

      this.text = data.text ?? '';

      if (data.attachments) {
        this.attachments = data.attachments.map(
          attachment => new RequisitionCommentAttachment(attachment)
        );
      }

      if (data.created_at) {
        this.created_at = data.created_at;
      }

      if (data.updated_at) {
        this.updated_at = data.updated_at;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  get isCommentEdited(): boolean {
    //milliseconds is different for both fields
    return (
      moment(this.created_at).format('YYYY-MM-DDTHH:mm:ss') !==
      moment(this.updated_at).format('YYYY-MM-DDTHH:mm:ss')
    );
  }
}
export class RequisitionApproval implements IRequisitionApproval {
  readonly id?: number;
  readonly approver: IUser;
  readonly status: RequisitionApprovalState;
  readonly created_at?: Date | string;
  readonly updated_at?: Date | string;

  constructor(data: IRequisitionApprovalData) {
    if (data.id) {
      this.id = data.id;
    }
    this.approver = new User(data.approver);

    this.status = data.status ?? RequisitionApprovalState.TENTATIVE;

    if (data.created_at) {
      this.created_at = data.created_at;
    }

    if (data.updated_at) {
      this.updated_at = data.updated_at;
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}
export class Requisition implements IRequisition {
  readonly id?: number;
  readonly amount: number = 0;
  readonly num_items: number = 0;
  readonly reason: string = '';
  readonly note: string = '';
  readonly project: string = '';
  readonly po_number: string = '';
  readonly po_not_required: boolean = false;
  readonly status: IRequisitionStatus = new RequisitionStatus();
  readonly currency?: ICurrency | number;
  readonly attachments: IRequisitionAttachment[] = [];
  readonly lines: IRequisitionLine[] = [];
  readonly supplier: IRequisitionSupplier = new RequisitionSupplier();
  readonly required_date?: Date | string;
  readonly is_urgent: boolean = false;
  readonly created_at?: Date | string;
  readonly updated_at?: Date | string;

  readonly approvers: RequisitionApproval[] = [];
  readonly buyer?: IUser | number;
  readonly owner?: IUser | number;

  constructor(data?: IRequisitionData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      // Need to check for undefined so that a value of zero would be treated
      // as a valid number
      if (typeof data.amount === 'number') {
        this.amount = data.amount;
      }

      if (typeof data.num_items == 'number') {
        this.num_items = data.num_items;
      }

      if (data.owner) {
        this.owner =
          typeof data.owner === 'number' ? data.owner : new User(data.owner);
      }

      if (data.reason) {
        this.reason = data.reason;
      }

      if (data.project) {
        this.project = data.project;
      }

      if (data.po_number) {
        this.po_number = data.po_number;
      }

      if (data.po_not_required) {
        this.po_not_required = data.po_not_required;
      }

      if (data.note) {
        this.note = data.note;
      }

      if (data.approvers) {
        this.approvers = data.approvers.map(
          approver => new RequisitionApproval(approver)
        );
      }

      if (data.buyer) {
        this.buyer =
          typeof data.buyer === 'number' ? data.buyer : new User(data.buyer);
      }

      if (data.status) {
        this.status = new RequisitionStatus(data.status);
      }

      if (data.currency) {
        this.currency =
          typeof data.currency === 'number'
            ? data.currency
            : new Currency(data.currency);
      }

      if (data.lines) {
        this.lines = data.lines.map(line => new RequisitionLine(line));
      }

      if (data.attachments) {
        this.attachments = data.attachments.map(
          attachment => new RequisitionAttachment(attachment)
        );
      }

      if (data.supplier) {
        this.supplier = new RequisitionSupplier(data.supplier);
      }

      if (data.required_date) {
        this.required_date = data.required_date;
      }

      this.is_urgent = data.is_urgent ?? false;

      if (data.created_at) {
        this.created_at = data.created_at;
      }

      if (data.updated_at) {
        this.updated_at = data.updated_at;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  addLine(line: IRequisitionLine): void {
    this.lines.push(line);
  }

  removeLine(index: number): void {
    this.lines.splice(index, 1);
  }

  attachFile(file: File): void {
    this.attachments.push(new RequisitionAttachment({ file: file }));
  }

  detachFile(file: string | File): void {
    const index = this.attachments?.findIndex(
      attachment => attachment.file == file
    );

    if (index > -1) {
      this.attachments?.splice(index, 1);
    }
  }
}

export class RequisitionSupplierAddressFieldError
  implements IRequisitionSupplierAddressFieldError {
  readonly address1: string[] = [];
  readonly address2: string[] = [];
  readonly city: string[] = [];
  readonly region: string[] = [];
  readonly country: string[] = [];
  readonly postal_code: string[] = [];

  constructor(data?: IRequisitionSupplierAddressFieldErrorData) {
    if (data) {
      if (data.address1) {
        this.address1 = data.address1;
      }

      if (data.address2) {
        this.address2 = data.address2;
      }

      if (data.city) {
        this.city = data.city;
      }

      if (data.region) {
        this.region = data.region;
      }

      if (data.country) {
        this.country = data.country;
      }

      if (data.postal_code) {
        this.postal_code = data.postal_code;
      }
    }
  }

  clearErrors(): void {
    this.address1.splice(0, this.address1.length);
    this.address2.splice(0, this.address2.length);
    this.city.splice(0, this.city.length);
    this.region.splice(0, this.region.length);
    this.country.splice(0, this.country.length);
    this.postal_code.splice(0, this.postal_code.length);
  }
}

export class RequisitionSupplierContactFieldError
  implements IRequisitionSupplierContactFieldError {
  readonly name: string[] = [];
  readonly email: string[] = [];
  readonly phone: string[] = [];

  constructor(data?: IRequisitionSupplierContactFieldErrorData) {
    if (data) {
      if (data.name) {
        this.name = data.name;
      }

      if (data.email) {
        this.email = data.email;
      }

      if (data.phone) {
        this.phone = data.phone;
      }
    }
  }

  clearErrors(): void {
    this.name.splice(0, this.name.length);
    this.email.splice(0, this.email.length);
    this.phone.splice(0, this.phone.length);
  }
}

export class RequisitionSupplierFieldError
  implements IRequisitionSupplierFieldError {
  readonly name: string[] = [];
  readonly vendor_code: string[] = [];
  readonly address: IRequisitionSupplierAddressFieldError = new RequisitionSupplierAddressFieldError();
  readonly contact: IRequisitionSupplierContactFieldError = new RequisitionSupplierContactFieldError();

  constructor(data?: IRequisitionSupplierFieldErrorData) {
    if (data) {
      if (data.name) {
        this.name = data.name;
      }

      if (data.vendor_code) {
        this.vendor_code = data.vendor_code;
      }

      if (data.address) {
        this.address = new RequisitionSupplierAddressFieldError(data.address);
      }

      if (data.contact) {
        if (Array.isArray(data.contact)) {
          // incase the servers returns the contat as an array only
          // HACK: we enforce what the display shows is jkust the fields inside are all errors
          // all mandatory
          this.contact = new RequisitionSupplierContactFieldError({
            name: data.contact
          });
        } else {
          this.contact = new RequisitionSupplierContactFieldError(data.contact);
        }
      }
    }
  }

  clearErrors(): void {
    this.name.splice(0, this.name.length);
    this.vendor_code.splice(0, this.vendor_code.length);
    this.address.clearErrors();
    this.contact.clearErrors();
  }
}

export class RequisitionLineFieldError implements IRequisitionLineFieldError {
  readonly item: string[] = [];
  readonly description: string[] = [];
  readonly quantity: string[] = [];
  readonly unit_price: string[] = [];
  readonly account: string[] = [];
  readonly department: string[] = [];

  constructor(data?: IRequisitionLineFieldErrorData) {
    if (data) {
      if (data.item) {
        this.item = data.item;
      }

      if (data.description) {
        this.description = data.description;
      }

      if (data.quantity) {
        this.quantity = data.quantity;
      }

      if (data.unit_price) {
        this.unit_price = data.unit_price;
      }

      if (data.account) {
        this.account = data.account;
      }

      if (data.department) {
        this.department = data.department;
      }
    }
  }

  clearErrors(): void {
    this.item.splice(0, this.item.length);
    this.description.splice(0, this.description.length);
    this.quantity.splice(0, this.quantity.length);
    this.unit_price.splice(0, this.unit_price.length);
    this.account.splice(0, this.account.length);
    this.department.splice(0, this.department.length);
  }

  static isRequisitionLineFieldError(
    data: IRequisitionLineFieldErrorData
  ): boolean {
    if (data) {
      if (data.item && Array.isArray(data.item)) {
        return true;
      } else if (data.description && Array.isArray(data.description)) {
        return true;
      } else if (data.quantity && Array.isArray(data.quantity)) {
        return true;
      } else if (data.unit_price && Array.isArray(data.unit_price)) {
        return true;
      } else if (data.account && Array.isArray(data.account)) {
        return true;
      } else if (data.department && Array.isArray(data.department)) {
        return true;
      }
    }

    return false;
  }
}

export class RequisitionFieldError implements IRequisitionFieldError {
  readonly currency: string[] = [];
  readonly reason: string[] = [];
  readonly project: string[] = [];
  readonly note: string[] = [];
  readonly required_date: string[] = [];
  readonly is_urgent: string[] = [];
  readonly po_number: string[] = [];
  readonly attachments: IFieldErrors[] = [];
  readonly supplier: IRequisitionSupplierFieldError = new RequisitionSupplierFieldError();
  readonly lines: IRequisitionLineFieldError[] = [];

  constructor(data?: IRequisitionFieldErrorData) {
    if (data) {
      if (data.currency) {
        this.currency = data.currency;
      }

      if (data.reason) {
        this.reason = data.reason;
      }

      if (data.project) {
        this.project = data.project;
      }

      if (data.note) {
        this.note = data.note;
      }

      if (data.required_date) {
        this.required_date = data.required_date;
      }

      if (data.is_urgent) {
        this.is_urgent = data.is_urgent;
      }
      if (data.po_number) {
        this.po_number = data.po_number;
      }

      if (data.attachments) {
        this.attachments = data.attachments.map(
          attachment => new FieldErrors(attachment)
        );
      }

      if (data.supplier) {
        if (Array.isArray(data.supplier)) {
          // incase the servers returns the contat as an array only
          // HACK: we enforce what the display shows is jkust the fields inside are all errors
          // all mandatory
          this.supplier = new RequisitionSupplierFieldError({
            name: data.supplier,
            contact: new RequisitionSupplierContactFieldError({
              name: data.supplier
            })
          });
        } else {
          this.supplier = new RequisitionSupplierFieldError(data.supplier);
        }
      }

      if (data.lines) {
        this.lines = data.lines.map(
          line => new RequisitionLineFieldError(line)
        );
      }
    }
  }

  clearErrors(): void {
    this.currency.splice(0, this.currency.length);
    this.project.splice(0, this.project.length);
    this.reason.splice(0, this.reason.length);
    this.note.splice(0, this.note.length);
    this.required_date.splice(0, this.required_date.length);
    this.is_urgent.splice(0, this.is_urgent.length);
    this.po_number.splice(0, this.po_number.length);
    this.attachments.splice(0, this.attachments.length);
    this.lines.splice(0, this.lines.length);
    this.supplier.clearErrors();
  }

  static isRequisitionFieldError(data: IRequisitionFieldErrorData): boolean {
    if (data.currency && Array.isArray(data.currency)) {
      return true;
    } else if (data.project && Array.isArray(data.project)) {
      return true;
    } else if (data.reason && Array.isArray(data.reason)) {
      return true;
    } else if (data.note && Array.isArray(data.note)) {
      return true;
    } else if (data.required_date && Array.isArray(data.required_date)) {
      return true;
    } else if (data.is_urgent && Array.isArray(data.is_urgent)) {
      return true;
    } else if (data.po_number && Array.isArray(data.po_number)) {
      return true;
    } else if (data.attachments && Array.isArray(data.attachments)) {
      return true;
    } else if (data.lines && Array.isArray(data.lines)) {
      return true;
    } else if (data.supplier) {
      return true;
    }

    return false;
  }
}
